import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import CoachCodePreview from "@/components/CoachCodePreview";
import { useCoachCodeLookup, normalizeCoachCode } from "@/hooks/useCoachCodeLookup";
import DailyCheckinCard from "@/components/DailyCheckinCard";
import WeeklyCheckinCard from "@/components/WeeklyCheckinCard";
import DailyCheckinReminderBanner from "@/components/DailyCheckinReminderBanner";
import TomorrowLoadsCard from "@/components/TomorrowLoadsCard";
import StudentBadgesPanel from "@/components/badges/StudentBadgesPanel";
import StudentOnboarding from "@/components/onboarding/StudentOnboarding";
import { isOnboardingComplete } from "@/lib/onboardingStorage";

import { getMeetingLink } from "@/lib/sessionUtils";
import {
  UtensilsCrossed, CalendarDays, MessageSquare,
  TrendingUp, CreditCard, UserPlus, UserCheck, ArrowUpRight,
  Zap, Scale, Target, Pencil, Trash2, Video, ExternalLink,
} from "lucide-react";
import { getDiscipline } from "@/lib/trainingDisciplines";
import DisciplineBadge from "@/components/training/DisciplineBadge";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

function coachPictureUrl(picture) {
  if (!picture) return undefined;
  if (picture.startsWith("http")) return picture;
  return `${API_BASE}${picture.startsWith("/") ? picture : `/${picture}`}`;
}

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return "Bonjour";
  if (h < 18) return "Bon après-midi";
  return "Bonsoir";
}

function StatCardSkeleton() {
  return (
    <Card className="border-slate-100">
      <CardContent className="p-5">
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <Skeleton className="h-3 w-20" />
            <Skeleton className="h-7 w-10" />
          </div>
          <Skeleton className="w-11 h-11 rounded-xl" />
        </div>
      </CardContent>
    </Card>
  );
}

function formatSessionDate(date, time) {
  if (!date) return "";
  try {
    const d = new Date(`${date}T${time || "00:00"}`);
    return (
      d.toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" }) +
      (time ? ` à ${time.slice(0, 5)}` : "")
    );
  } catch {
    return `${date}${time ? " à " + time : ""}`;
  }
}

export default function StudentDashboard() {
  const { user, refreshUser } = useAuth();
  const [stats, setStats] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [lastPayments, setLastPayments] = useState([]);
  const [coachCode, setCoachCode] = useState("");
  const [linkingCoach, setLinkingCoach] = useState(false);
  const [loading, setLoading] = useState(true);
  const [goal, setGoal] = useState(null);
  const [goalOpen, setGoalOpen] = useState(false);
  const [goalForm, setGoalForm] = useState({ goal_weight: "", goal_date: "", goal_note: "" });
  const [savingGoal, setSavingGoal] = useState(false);
  const [myCoach, setMyCoach] = useState(null);
  const [coachLoading, setCoachLoading] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const checkinRef = useRef(null);
  const navigate = useNavigate();
  const { preview: coachPreview, checking: coachChecking } = useCoachCodeLookup(coachCode);

  useEffect(() => {
    if (user?.user_id && user?.role === "student" && !isOnboardingComplete(user.user_id)) {
      setShowOnboarding(true);
    }
  }, [user?.user_id, user?.role]);

  useEffect(() => {
    if (!user?.coach_id) {
      setMyCoach(null);
      return;
    }
    setCoachLoading(true);
    api.get("/students/my-coach")
      .then((r) => setMyCoach(r.data))
      .catch(() => setMyCoach(user.coach || null))
      .finally(() => setCoachLoading(false));
  }, [user?.coach_id, user?.coach]);

  useEffect(() => {
    Promise.all([
      api.get('/dashboard/stats').catch(() => ({ data: null })),
      api.get('/coaching-sessions').catch(() => ({ data: [] })),
      api.get('/payments/history').catch(() => ({ data: [] })),
    ]).then(([statsRes, sessionsRes, paymentsRes]) => {
      setStats(statsRes.data);
      setSessions((sessionsRes.data || []).filter(s => s.status === 'scheduled').slice(0, 3));
      setLastPayments((paymentsRes.data || []).slice(0, 3));
      setLoading(false);
    });
    // Fetch goal
    api.get('/students/goal').then(r => {
      if (r.data.goal_weight) setGoal(r.data);
    }).catch(() => {});
  }, []);

  const handleLinkCoach = async (e) => {
    e.preventDefault();
    if (!coachCode.trim()) return;
    setLinkingCoach(true);
    try {
      const { data } = await api.post('/students/link-coach', { coach_code: coachCode.trim().toUpperCase() });
      toast.success("Coach associé avec succès !");
      setCoachCode("");
      if (data?.coach) setMyCoach(data.coach);
      await refreshUser();
    } catch (err) {
      const msg = err.response?.data?.detail || "Code coach invalide";
      toast.error(msg);
    } finally {
      setLinkingCoach(false);
    }
  };

  const handleSaveGoal = async (e) => {
    e.preventDefault();
    if (!goalForm.goal_weight) return;
    setSavingGoal(true);
    try {
      const { data } = await api.put('/students/goal', goalForm);
      setGoal(data);
      setGoalOpen(false);
      toast.success("Objectif enregistré !");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally { setSavingGoal(false); }
  };

  const handleDeleteGoal = async () => {
    try {
      await api.delete('/students/goal');
      setGoal(null);
      toast.success("Objectif supprimé");
    } catch { toast.error("Erreur"); }
  };

  const openEditGoal = () => {
    setGoalForm({
      goal_weight: goal?.goal_weight || "",
      goal_date: goal?.goal_date || "",
      goal_note: goal?.goal_note || "",
    });
    setGoalOpen(true);
  };

  // Goal progress calculation
  const getGoalProgress = () => {
    if (!goal?.goal_weight || !stats?.latest_progress?.weight) return null;
    const current = stats.latest_progress.weight;
    const target = goal.goal_weight;
    // Assume first log weight is start (or current if gaining)
    const start = stats.latest_progress.first_weight || current;
    const totalDiff = Math.abs(start - target);
    if (totalDiff === 0) return 100;
    const currentDiff = Math.abs(current - target);
    const progress = Math.max(0, Math.min(100, ((totalDiff - currentDiff) / totalDiff) * 100));
    return Math.round(progress);
  };

  const goalProgress = getGoalProgress();
  const daysRemaining = goal?.goal_date ? Math.max(0, Math.ceil((new Date(goal.goal_date) - new Date()) / 86400000)) : null;

  const activeTraining = stats?.active_training;
  const trainingMeta = getDiscipline(activeTraining?.discipline || "strength");
  const TrainingIcon = trainingMeta.icon;

  const cards = stats ? [
    { label: "Plans Diet", value: stats.diet_plans_count, icon: UtensilsCrossed, color: "bg-green-50 text-green-600", accent: "border-l-green-500", path: "/student/diet-plan" },
    {
      label: activeTraining ? trainingMeta.shortLabel : "Entraînement",
      value: stats.training_plans_count,
      icon: TrainingIcon,
      color: activeTraining ? trainingMeta.light : "bg-orange-50 text-orange-600",
      accent: "border-l-orange-500",
      path: "/student/training-plan",
      subtitle: activeTraining?.title,
      iconAccent: trainingMeta.accent,
    },
    { label: "Séances", value: stats.sessions_count, icon: CalendarDays, color: "bg-purple-50 text-purple-600", accent: "border-l-purple-500", path: null },
    { label: "Messages", value: stats.unread_messages, icon: MessageSquare, color: "bg-pink-50 text-pink-600", accent: "border-l-pink-500", path: "/student/messages" },
    { label: "Paiements", value: lastPayments.length, icon: CreditCard, color: "bg-cyan-50 text-cyan-600", accent: "border-l-cyan-500", path: "/student/payments" },
  ] : [];

  const quickLinks = [
    { label: "Progression", icon: TrendingUp, path: "/student/progress", color: "text-blue-600 bg-blue-50 hover:bg-blue-100" },
    { label: trainingMeta.shortLabel, icon: TrainingIcon, path: "/student/training-plan", color: `${trainingMeta.accent} ${trainingMeta.light} hover:opacity-90` },
    { label: "Diet", icon: UtensilsCrossed, path: "/student/diet-plan", color: "text-green-600 bg-green-50 hover:bg-green-100" },
    { label: "Messages", icon: MessageSquare, path: "/student/messages", color: "text-pink-600 bg-pink-50 hover:bg-pink-100" },
  ];

  const scrollToCheckin = () => {
    checkinRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  return (
    <div data-testid="student-dashboard">
      <StudentOnboarding open={showOnboarding} onComplete={() => setShowOnboarding(false)} />

      {/* Header */}
      <div>
        <p className="text-sm font-medium text-blue-600 uppercase tracking-widest mb-1 flex items-center gap-1">
          <Zap className="w-3.5 h-3.5" />
          {getGreeting()}{user?.name ? `, ${user.name.split(" ")[0]}` : ""}
        </p>
        <h1 className="font-heading text-3xl font-bold tracking-tight uppercase text-slate-900">Mon Dashboard</h1>
        <p className="text-slate-400 mt-1 text-sm">
          {new Date().toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
        </p>
      </div>

      {user?.coach_id && (
        <Card className="border-amber-200 bg-amber-50/40 mt-6 cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate("/student/today")}>
          <CardContent className="p-4 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                <Zap className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-900">Session du jour</p>
                <p className="text-xs text-slate-500">Séance, repas et compléments — tout en un coup d&apos;œil</p>
              </div>
            </div>
            <ArrowUpRight className="w-5 h-5 text-amber-600 shrink-0" />
          </CardContent>
        </Card>
      )}
      {user?.coach_id && <DailyCheckinReminderBanner onCheckinClick={scrollToCheckin} />}
      {user?.coach_id && <TomorrowLoadsCard />}
      {user?.coach_id && <div className="mt-6"><StudentBadgesPanel /></div>}

      {/* Coach section */}
      {!user?.coach_id ? (
        <Card className="border-orange-200 bg-orange-50/50 mt-6" data-testid="link-coach-card">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center flex-shrink-0">
                <UserPlus className="w-6 h-6 text-orange-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-heading text-lg font-semibold text-slate-900">Pas encore de coach ?</h3>
                <p className="text-sm text-slate-500 mt-1">Entrez le code de votre coach pour accéder à ses plans et ses services.</p>
                <form onSubmit={handleLinkCoach} className="mt-4 max-w-md space-y-2">
                  <div className="flex gap-3">
                    <Input
                      value={coachCode}
                      onChange={(e) => setCoachCode(normalizeCoachCode(e.target.value))}
                      placeholder="COACH-XXXXXX"
                      className="h-11 bg-white uppercase font-mono tracking-wider"
                      data-testid="coach-code-input"
                    />
                    <Button
                      type="submit"
                      disabled={linkingCoach || !coachCode.trim()}
                      className="bg-orange-500 hover:bg-orange-600 text-white rounded-full h-11 px-6 shrink-0"
                      data-testid="link-coach-btn"
                    >
                      {linkingCoach ? "..." : "Rejoindre"}
                    </Button>
                  </div>
                  <CoachCodePreview preview={coachPreview} checking={coachChecking} codeLength={coachCode.trim().length} />
                </form>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-blue-200 bg-blue-50/50 mt-6" data-testid="my-coach-card">
          <CardContent className="p-5">
            {coachLoading ? (
              <div className="flex items-center gap-4">
                <Skeleton className="w-12 h-12 rounded-xl" />
                <div className="space-y-2 flex-1">
                  <Skeleton className="h-3 w-20" />
                  <Skeleton className="h-5 w-36" />
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-4">
                <Avatar className="w-12 h-12 rounded-xl flex-shrink-0">
                  <AvatarImage src={coachPictureUrl(myCoach?.picture)} alt={myCoach?.name} />
                  <AvatarFallback className="rounded-xl bg-blue-100 text-blue-600 font-semibold">
                    {myCoach?.name?.[0]?.toUpperCase() || <UserCheck className="w-5 h-5" />}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-slate-400 uppercase tracking-wider font-medium">Mon coach</p>
                  <p className="text-base font-semibold text-slate-900 truncate">{myCoach?.name || user.coach?.name || "Coach"}</p>
                  {myCoach?.punch_line && (
                    <p className="text-sm text-slate-500 truncate mt-0.5">{myCoach.punch_line}</p>
                  )}
                  {myCoach?.pseudo && (
                    <p className="text-xs text-blue-600 mt-0.5">@{myCoach.pseudo}</p>
                  )}
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate("/student/my-coach")}
                    className="rounded-full text-xs h-8"
                  >
                    Voir profil
                  </Button>
                  {myCoach?.public_url && (
                    <a
                      href={myCoach.public_url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700 px-2"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Daily & weekly check-ins */}
      {user?.coach_id && (
        <div className="grid lg:grid-cols-2 gap-4 mt-6">
          <div ref={checkinRef}>
            <DailyCheckinCard compact />
          </div>
          <WeeklyCheckinCard />
        </div>
      )}

      {/* Quick Links */}
      <div className="grid grid-cols-4 gap-3 mt-6">
        {quickLinks.map((a, i) => (
          <button
            key={i}
            onClick={() => navigate(a.path)}
            className={`flex flex-col items-center justify-center gap-2 p-4 rounded-2xl font-semibold text-xs transition-colors ${a.color}`}
            data-testid={`quick-link-${i}`}
          >
            <a.icon className="w-5 h-5" />
            {a.label}
          </button>
        ))}
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mt-6">
        {loading
          ? Array.from({ length: 5 }).map((_, i) => <StatCardSkeleton key={i} />)
          : cards.map((s, i) => (
            <Card
              key={i}
              className={`border-slate-100 border-l-4 ${s.accent} hover:shadow-md transition-shadow ${s.path ? 'cursor-pointer' : ''}`}
              onClick={() => s.path && navigate(s.path)}
              data-testid={`student-stat-${i}`}
            >
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-400 uppercase tracking-wider font-medium">{s.label}</p>
                    <p className="text-2xl font-bold text-slate-900 mt-1">{s.value ?? "—"}</p>
                    {s.subtitle && (
                      <p className="text-[10px] text-slate-400 mt-0.5 truncate max-w-[120px]">{s.subtitle}</p>
                    )}
                  </div>
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${s.color}`}>
                    <s.icon className={`w-5 h-5 ${s.iconAccent || ""}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        }
      </div>

      {/* Goal section */}
      <div className="mt-6">
        {!goal ? (
          <Dialog open={goalOpen} onOpenChange={setGoalOpen}>
            <DialogTrigger asChild>
              <button
                onClick={() => setGoalForm({ goal_weight: "", goal_date: "", goal_note: "" })}
                className="w-full flex items-center gap-4 p-5 rounded-2xl border-2 border-dashed border-slate-200 hover:border-blue-300 hover:bg-blue-50/30 transition-colors group"
              >
                <div className="w-11 h-11 rounded-xl bg-slate-100 group-hover:bg-blue-100 flex items-center justify-center flex-shrink-0 transition-colors">
                  <Target className="w-5 h-5 text-slate-400 group-hover:text-blue-600 transition-colors" />
                </div>
                <div className="text-left">
                  <p className="text-sm font-semibold text-slate-700">Définir un objectif</p>
                  <p className="text-xs text-slate-400">Poids cible, date, et suivez votre progression</p>
                </div>
              </button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-heading text-xl">Mon objectif</DialogTitle></DialogHeader>
              <form onSubmit={handleSaveGoal} className="space-y-4 mt-4">
                <div><Label>Poids cible (kg)</Label><Input type="number" step="0.1" value={goalForm.goal_weight} onChange={e => setGoalForm(f => ({ ...f, goal_weight: e.target.value }))} className="mt-1.5 h-11" required /></div>
                <div><Label>Date cible (optionnel)</Label><Input type="date" value={goalForm.goal_date} onChange={e => setGoalForm(f => ({ ...f, goal_date: e.target.value }))} className="mt-1.5 h-11" /></div>
                <div><Label>Note (optionnel)</Label><Textarea value={goalForm.goal_note} onChange={e => setGoalForm(f => ({ ...f, goal_note: e.target.value }))} className="mt-1.5" placeholder="Ma motivation..." /></div>
                <Button type="submit" disabled={savingGoal} className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-full h-11">{savingGoal ? "..." : "Enregistrer"}</Button>
              </form>
            </DialogContent>
          </Dialog>
        ) : (
          <Card className="border-blue-100 bg-gradient-to-r from-blue-50/50 to-indigo-50/50">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                    <Target className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-400 uppercase tracking-wider font-medium">Mon objectif</p>
                    <p className="text-lg font-bold text-slate-900">{goal.goal_weight} <span className="text-sm font-medium text-slate-400">kg</span></p>
                  </div>
                </div>
                <div className="flex gap-1">
                  <Dialog open={goalOpen} onOpenChange={setGoalOpen}>
                    <DialogTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={openEditGoal}><Pencil className="w-3.5 h-3.5" /></Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader><DialogTitle className="font-heading text-xl">Modifier l'objectif</DialogTitle></DialogHeader>
                      <form onSubmit={handleSaveGoal} className="space-y-4 mt-4">
                        <div><Label>Poids cible (kg)</Label><Input type="number" step="0.1" value={goalForm.goal_weight} onChange={e => setGoalForm(f => ({ ...f, goal_weight: e.target.value }))} className="mt-1.5 h-11" required /></div>
                        <div><Label>Date cible</Label><Input type="date" value={goalForm.goal_date} onChange={e => setGoalForm(f => ({ ...f, goal_date: e.target.value }))} className="mt-1.5 h-11" /></div>
                        <div><Label>Note</Label><Textarea value={goalForm.goal_note} onChange={e => setGoalForm(f => ({ ...f, goal_note: e.target.value }))} className="mt-1.5" /></div>
                        <Button type="submit" disabled={savingGoal} className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-full h-11">{savingGoal ? "..." : "Enregistrer"}</Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-red-400 hover:text-red-600" onClick={handleDeleteGoal}><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              </div>
              {/* Progress bar */}
              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-slate-500">Poids actuel : <strong>{stats?.latest_progress?.weight ?? "—"} kg</strong></span>
                  {daysRemaining !== null && <span className="text-slate-500">{daysRemaining > 0 ? `${daysRemaining}j restants` : "Date atteinte"}</span>}
                </div>
                <div className="w-full bg-slate-200 rounded-full h-3 overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full transition-all duration-700" style={{ width: `${goalProgress ?? 0}%` }} />
                </div>
                <div className="flex items-center justify-between text-xs text-slate-400">
                  <span>{goalProgress ?? 0}% atteint</span>
                  {goal.goal_note && <span className="italic truncate ml-4">"{goal.goal_note}"</span>}
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mt-8">
        {/* Latest progress */}
        <Card className="border-slate-100">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-heading text-lg font-semibold text-slate-900 uppercase tracking-tight">Ma progression</h3>
              <button onClick={() => navigate('/student/progress')} className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 font-medium" data-testid="view-progress-btn">
                Voir tout <ArrowUpRight className="w-3.5 h-3.5" />
              </button>
            </div>
            {loading ? (
              <div className="space-y-3">
                <Skeleton className="h-16 w-full rounded-xl" />
                <Skeleton className="h-4 w-3/4" />
              </div>
            ) : stats?.latest_progress ? (
              <div className="space-y-3">
                <div className="flex items-center gap-4 p-4 rounded-xl bg-slate-50">
                  <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <Scale className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">
                      {stats.latest_progress.weight} <span className="text-base font-medium text-slate-400">kg</span>
                    </p>
                    <p className="text-xs text-slate-400">{stats.latest_progress.date}</p>
                  </div>
                </div>
                {stats.latest_progress.notes && (
                  <p className="text-sm text-slate-500 px-1">{stats.latest_progress.notes}</p>
                )}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-3">
                  <TrendingUp className="w-5 h-5 text-slate-400" />
                </div>
                <p className="text-sm text-slate-400">Aucune donnée de progression</p>
                <button onClick={() => navigate('/student/progress')} className="mt-3 text-sm text-blue-600 hover:underline font-medium">
                  Commencer à tracker
                </button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Upcoming sessions */}
        <Card className="border-slate-100">
          <CardContent className="p-6">
            <h3 className="font-heading text-lg font-semibold text-slate-900 uppercase tracking-tight mb-5">Prochaines séances</h3>
            {loading ? (
              <div className="space-y-3">
                {Array.from({ length: 2 }).map((_, i) => (
                  <div key={i} className="flex gap-3 p-3">
                    <Skeleton className="w-9 h-9 rounded-xl flex-shrink-0" />
                    <div className="space-y-1.5 flex-1">
                      <Skeleton className="h-3.5 w-32" />
                      <Skeleton className="h-3 w-28" />
                    </div>
                  </div>
                ))}
              </div>
            ) : sessions.length === 0 ? (
              <div className="text-center py-8">
                <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-3">
                  <CalendarDays className="w-5 h-5 text-slate-400" />
                </div>
                <p className="text-sm text-slate-400">Aucune séance prévue</p>
              </div>
            ) : (
              <div className="space-y-3">
                {sessions.map(s => {
                  const meetingLink = getMeetingLink(s);
                  return (
                  <div key={s.session_id} className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 hover:bg-purple-50/50 transition-colors">
                    <div className="w-9 h-9 rounded-xl bg-purple-100 flex items-center justify-center flex-shrink-0">
                      <CalendarDays className="w-4 h-4 text-purple-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-900 truncate">{s.title}</p>
                      <p className="text-xs text-slate-400">{formatSessionDate(s.date, s.time)}</p>
                      {meetingLink && (
                        <a href={meetingLink} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-purple-600 hover:underline mt-1 font-medium">
                          <Video className="w-3 h-3" /> Rejoindre la visio
                        </a>
                      )}
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
